import { useState } from "react";
import { Box, Button, Table, TableCell, TableHead, TableRow } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Fab from "@mui/material/Fab";
import EditIcon from "@mui/icons-material/Edit";
import { BASE_URL } from "../../../../services/client/rest";
import axios from "axios";
import PopoverComponent from "../Popover/PopoverComponent";
import { useQuery } from "react-query";
import AlertApi from "../../../../services/API/Alert.Api";

const ServerAlerts = () => {
  const [sreverRow, SetServerRow] = useState([] as any);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [name, SetName] = useState("");
  const [note, SetNote] = useState("");
  const open = Boolean(anchorEl);

  // Group comment state:
  const [groupAnchorEl, setGroupAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [groupName, SetGroupName] = useState("");
  const [groupNote, SetGroupNote] = useState("");
  const [isGroupSaving, SetIsGroupSaving] = useState(false);
  const groupOpen = Boolean(groupAnchorEl);

  const classes = useStyles();

  var bearer = "Bearer " + localStorage.getItem("token");
  const headers = {
    Authorization: bearer,
  };

  //---------------------- Fetching Live Alert Data ----------------------//
  const { data, refetch } = useQuery(
    ["GetServerAlerts"],
    () => AlertApi.getServerAlerts(),
    {
      initialData: [],
      refetchInterval: 2000 * 60 * 1,
      refetchIntervalInBackground: true,
    }
  );

  const handleServerRowClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    currentRow: any
  ) => {
    setAnchorEl(event.currentTarget);
    SetServerRow(currentRow);
  };

  // Build a tech-representative string from name + note, skipping empty
  // values so we never save a lone ",".
  const buildTechRepresentative = (n: string, nt: string) => {
    const parts = [n, nt]
      .map((p) => (p || "").trim())
      .filter((p) => p.length > 0);
    return parts.join(",");
  };

  // Submit Handler:
  function OnSaveClick() {
    const techRep = buildTechRepresentative(name, note);
    if (techRep === "") {
      // Nothing to save — close the popover and bail out.
      setAnchorEl(null);
      SetName("");
      SetNote("");
      return;
    }

    const request = {
      id: sreverRow.id,
      type: sreverRow.type,
      server: sreverRow.server,
      acknowledge: true,
      techRepresentative: techRep,
    };

    axios
      .post(BASE_URL + "ServerInfo/AckServerAlert", request, {
        headers,
      })
      .then((response) => {
        setAnchorEl(null); // Close Popover:
        SetName("");
        SetNote("");
        refetch(); // Refetch Alert data:
      })
      .catch((err) => {
        console.log(err);
        setAnchorEl(null); // Close Popover:
      });
  }

  // Open group comment popover:
  const handleGroupCommentClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setGroupAnchorEl(event.currentTarget);
  };

  // Returns rows that have no existing comment / tech representative.
  const getRowsWithoutComment = () => {
    if (!data || data.length === 0) return [];
    return data.filter(
      (row: any) =>
        row.techRepresentative === null ||
        row.techRepresentative === undefined ||
        String(row.techRepresentative).trim() === ""
    );
  };

  // Group submit handler — applies the comment only to rows without an
  // existing comment. Rows with comments remain unchanged.
  async function OnGroupSaveClick() {
    const rowsToUpdate = getRowsWithoutComment();
    if (rowsToUpdate.length === 0) {
      setGroupAnchorEl(null);
      SetGroupName("");
      SetGroupNote("");
      return;
    }

    const techRep = buildTechRepresentative(groupName, groupNote);
    if (techRep === "") {
      // Nothing to save — close the popover and bail out.
      setGroupAnchorEl(null);
      SetGroupName("");
      SetGroupNote("");
      return;
    }

    SetIsGroupSaving(true);
    try {
      await Promise.all(
        rowsToUpdate.map((row: any) => {
          const request = {
            id: row.id,
            type: row.type,
            server: row.server,
            acknowledge: true,
            techRepresentative: techRep,
          };
          return axios.post(BASE_URL + "ServerInfo/AckServerAlert", request, {
            headers,
          });
        })
      );
    } catch (err) {
      console.log(err);
    } finally {
      SetIsGroupSaving(false);
      setGroupAnchorEl(null);
      SetGroupName("");
      SetGroupNote("");
      refetch();
    }
  }

  const rowsWithoutCommentCount = getRowsWithoutComment().length;

  return (
    <Box
      style={{ display: "table", marginTop: "20px", minWidth: "100%" }}
      alignItems="center"
    >
      <fieldset
        style={{
          fontSize: "large",
          fontWeight: "bold",
          border: "1px solid silver",
        }}
      >
        <legend>&nbsp;Server Alerts&nbsp;</legend>
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          style={{ padding: "4px 4px 8px 4px" }}
        >
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon style={{ fontSize: "16px" }} />}
            disabled={
               isGroupSaving
            }
            //  disabled={
            //   rowsWithoutCommentCount === 0 || isGroupSaving
            // }
            onClick={handleGroupCommentClick}
            title={
              rowsWithoutCommentCount === 0
                ? "All rows already have comments"
                : `Apply a comment to ${rowsWithoutCommentCount} row(s) without a comment`
            }
          >
            Group Comment
            {rowsWithoutCommentCount > 0
              ? ` (${rowsWithoutCommentCount})`
              : ""}
          </Button>
        </Box>
        <Table aria-label="simple table" className={"myTable"}>
          <TableHead>
            <TableRow>
              <TableCell>Server</TableCell>
              <TableCell>SpoolerStatus</TableCell>
              <TableCell>CPU(%)</TableCell>
              <TableCell>Memory Usage(%)</TableCell>
              <TableCell>AvailableDiskSpace</TableCell>
              <TableCell>LastUpdate</TableCell>
              <TableCell>Acknowledge</TableCell>
              <TableCell>EmailSent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>TechSupport</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>

          {data.length > 0 ? (
            data.map((row: any, index: any) => {
              let lastUpdate = "";
              if (row.lastUpdate != null)
                lastUpdate = new Date(row.lastUpdate).toLocaleDateString();

              let classAlert = "";
              if (row.alertCause === "Low Disk Space.")
                classAlert = classes.alert;

              let cpuAlert = "";
              if (parseInt(row.cpu.replace("%", "")) >= 50)
                cpuAlert = classes.alertMedium;
              if (parseInt(row.cpu.replace("%", "")) >= 75)
                cpuAlert = classes.alert;

              let serviceAlert = "";
              if (row.spoolerStatus === "Stopped") serviceAlert = classes.alert;

              let memoryAlert = "";
              if (row.alertCause === "Low RAM Space")
                memoryAlert = classes.alert;

              let statusAlert = "";
              if (row.status === "Unavailable" || row.alertCause === "Sql Service is Down") statusAlert = classes.alert;

              return (
                <TableRow
                  hover
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  role="checkbox"
                >
                  <TableCell>{row.server}</TableCell>
                  <TableCell className={serviceAlert}>
                    {row.spoolerStatus}
                  </TableCell>
                  <TableCell className={cpuAlert}>{row.cpu}</TableCell>
                  <TableCell className={memoryAlert}>{row.memory}</TableCell>
                  <TableCell className={classAlert}>
                    {row.availableDiskSpace}
                  </TableCell>
                  <TableCell>{lastUpdate}</TableCell>
                  {row.acknowledge === true ? (
                    <TableCell>Yes</TableCell>
                  ) : (
                    <TableCell>No</TableCell>
                  )}
                  {row.isEmailSent === true ? (
                    <TableCell>Yes</TableCell>
                  ) : (
                    <TableCell>No</TableCell>
                  )}
                  <TableCell className={statusAlert}>{row.alertCause === "Sql Service is Down"? "Sql Service is Down" : row.status}</TableCell>
                  <TableCell>{row.techRepresentative}</TableCell>
                  <TableCell>
                    <Fab
                      size="small"
                      aria-label="edit"
                      onClick={(event) => handleServerRowClick(event, row)}
                    >
                      <EditIcon style={{ fontSize: "18px" }} />
                    </Fab>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow
              hover
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              role="checkbox"
            >
              <TableCell
                align="center"
                colSpan={10}
                style={{
                  fontSize: "18pt",
                  fontWeight: "bold",
                }}
              >
                No Record Found
              </TableCell>
            </TableRow>
          )}
        </Table>
      </fieldset>

      <PopoverComponent
        open={open}
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        SetName={SetName}
        SetNote={SetNote}
        OnSaveClick={OnSaveClick}
      />

      <PopoverComponent
        open={groupOpen}
        anchorEl={groupAnchorEl}
        setAnchorEl={setGroupAnchorEl}
        SetName={SetGroupName}
        SetNote={SetGroupNote}
        OnSaveClick={OnGroupSaveClick}
      />
    </Box>
  );
};

export default ServerAlerts;

const useStyles = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
  },
  alert: { backgroundColor: "#E74C3C", color: "white" },
  alertMedium: { backgroundColor: "orange", color: "white !important" },
  hideCell: {
    display: "none !important",
  },
}));
